import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SchoolDatePicker from '../../components/SchoolDatePicker/SchoolDatePicker';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import VisibilityIcon from '@material-ui/icons/Visibility';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import TodayReportService from './Locality/Service/todayReportService';
import AttendenceService from './Locality/Service/attendenceService';
import AddClassService from './Locality/Service/addClassService';
import SectionService from './Locality/Service/sectionService';

const useStyles = makeStyles((theme) => ({
  pageHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  pageTitle: {
    fontWeight: 700,
    fontSize: '1.9rem',
    color: '#1a1a1a',
    lineHeight: 1.2,
  },
  pageSubtitle: {
    color: '#555',
    fontSize: '0.9rem',
    marginTop: 2,
  },
  filtersCard: {
    borderRadius: 14,
    border: '1px solid #d9d9d9',
    boxShadow: 'none',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  tableCard: {
    borderRadius: 14,
    border: '1px solid #d9d9d9',
    boxShadow: 'none',
    overflow: 'hidden',
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 700,
    color: '#1a1a1a',
    fontSize: '1rem',
    padding: theme.spacing(2),
    borderBottom: '1px solid #d9d9d9',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    borderBottom: '1px solid #d9d9d9',
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: '#fafafa',
    },
  },
  sectionHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sectionCountChip: {
    height: 22,
    fontSize: '0.75rem',
    fontWeight: 700,
    backgroundColor: '#e6e6e6',
    color: '#1a1a1a',
  },
  attendanceHeadCell: {
    backgroundColor: '#1a1a1aad',
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.9rem',
    textAlign: 'center',
  },
  attendanceBodyCell: {
    fontSize: '18px',
    textAlign: 'center',
  },
  attendanceCountCell: {
    fontSize: '20px',
    textAlign: 'center',
  },
  presentCount: {
    color: 'green',
    fontSize: '20px',
    textAlign: 'center',
  },
  absentCount: {
    cursor: 'pointer',
    color: 'red',
    fontSize: '20px',
    textAlign: 'center',
  },
  absentBox: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(2),
    width: '60%',
    maxWidth: 560,
    backgroundColor: '#fff0f0',
    border: '1px solid #ffcccc',
    borderRadius: 8,
    boxShadow: theme.shadows[2],
    textAlign: 'left',
  },
  summaryChip: {
    backgroundColor: '#e6e6e6',
    color: '#1a1a1a',
    fontWeight: 700,
    borderRadius: 8,
  },
  iconBtnOutlined: {
    border: '1px solid #8f8f8f',
    borderRadius: 8,
    padding: 8,
    color: '#444',
  },
  viewBtn: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 8,
    color: '#1a1a1a',
    border: '1px solid #c4c4c4',
    backgroundColor: '#e6e6e6',
    padding: '2px 10px',
    minWidth: 0,
    '&:hover': {
      backgroundColor: '#e0ecff',
    },
  },
  updatedTodayChip: {
    height: 20,
    fontSize: '0.68rem',
    fontWeight: 700,
    backgroundColor: '#fff4e5',
    color: '#e08600',
    marginLeft: theme.spacing(1),
  },
}));

const headerCellStyle = {
  backgroundColor: '#efefef',
  color: '#444',
  fontWeight: 500,
    fontSize: '0.85rem',
  borderBottom: '1px solid #d9d9d9',
};

const toIsoDate = (d = new Date()) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
};

const getCreatedByLabel = (row) => {
  if (row?.teacherName) return row.teacherName;
  const createdBy = row?.createdBy;
  if (!createdBy) return '—';
  if (createdBy.teacher?.teacherName) return createdBy.teacher.teacherName;
  if (createdBy.school?.schooleName) return createdBy.school.schooleName;
  return createdBy.userName || createdBy.serialNumber || '—';
};

const getUpdatedByLabel = (row) => {
  if (!row?.wasUpdated) return '—';
  if (row?.updatedByName) return row.updatedByName;
  const updatedBy = row?.updatedBy;
  if (!updatedBy) return '—';
  if (updatedBy.teacher?.teacherName) return updatedBy.teacher.teacherName;
  if (updatedBy.school?.schooleName) return updatedBy.school.schooleName;
  return updatedBy.userName || updatedBy.serialNumber || '—';
};

const paginateRows = (rows, page, rowsPerPage) =>
  rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

function SectionHeader({ title, count, expanded, onToggle }) {
  const classes = useStyles();
  return (
    <Box
      className={classes.sectionHeader}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      aria-expanded={expanded}
    >
      <Box className={classes.sectionHeaderLeft}>
        {expanded ? (
          <ExpandMoreIcon fontSize="small" style={{ color: '#444' }} />
        ) : (
          <ChevronRightIcon fontSize="small" style={{ color: '#444' }} />
        )}
        <Typography className={classes.sectionTitle} style={{ padding: 0, border: 'none' }}>
          {title}
        </Typography>
        <Chip size="small" className={classes.sectionCountChip} label={count} />
      </Box>
      <Typography variant="caption" style={{ color: '#8a8a8a' }}>
        {expanded ? 'Collapse' : 'Expand'}
      </Typography>
    </Box>
  );
}

export default function TodayTeacherReport() {
  const classes = useStyles();
  const userDetails = useMemo(
    () => JSON.parse(localStorage.getItem('userDetail') || '{}'),
    []
  );

  const [reportDate, setReportDate] = useState(toIsoDate());
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [addClassList, setAddClassList] = useState([]);
  const [sectionList, setSectionList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState({
    attendances: [],
    activities: [],
    assignments: [],
    summary: { attendanceCount: 0, activityCount: 0, assignmentCount: 0 },
  });

  const [activityPage, setActivityPage] = useState(0);
  const [activityRowsPerPage, setActivityRowsPerPage] = useState(10);
  const [assignmentPage, setAssignmentPage] = useState(0);
  const [assignmentRowsPerPage, setAssignmentRowsPerPage] = useState(10);
  const [attendancePage, setAttendancePage] = useState(0);
  const [attendanceRowsPerPage, setAttendanceRowsPerPage] = useState(10);

  const [studentsDialog, setStudentsDialog] = useState({
    open: false,
    title: '',
    type: 'assignment',
    students: [],
  });

  const [absentStudentList, setAbsentStudentList] = useState([]);
  const [showAbsentKey, setShowAbsentKey] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    attendance: false,
    activities: false,
    assignments: false,
  });

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const loadClasses = useCallback(() => {
    if (!userDetails?.schooleId) return;
    AddClassService.getAllAddClass(userDetails.schooleId)
      .then((res) => setAddClassList(Array.isArray(res) ? res : []))
      .catch(() => setAddClassList([]));
  }, [userDetails?.schooleId]);

  const loadSections = useCallback((nextClassId) => {
    if (!nextClassId) {
      setSectionList([]);
      return;
    }
    SectionService.findSectionList({ classId: nextClassId, schooleId: userDetails.schooleId })
      .then((res) => setSectionList(Array.isArray(res) ? res : []))
      .catch(() => setSectionList([]));
  }, [userDetails?.schooleId]);

  const handleAbsentClick = (row) => {
    if (!userDetails?.schooleId || !row?.classId) return;
    const key = `${row.classId}-${row.sectionId || 'all'}`;
    AttendenceService.getAbsentStudents({
      schooleId: userDetails.schooleId,
      classId: row.classId,
      ...(row.sectionId ? { sectionId: row.sectionId } : {}),
      date: reportDate,
    })
      .then((res) => {
        setAbsentStudentList(Array.isArray(res) ? res : []);
        setShowAbsentKey(key);
      })
      .catch(() => {
        setAbsentStudentList([]);
        setShowAbsentKey(key);
      });
  };

  const loadReport = useCallback(() => {
    if (!userDetails?.schooleId) return;
    setLoading(true);
    TodayReportService.getTodayTeacherWork({
      schooleId: userDetails.schooleId,
      date: reportDate,
      ...(classId ? { classId } : {}),
      ...(sectionId ? { sectionId } : {}),
    })
      .then((res) => {
        setReport({
          attendances: res?.attendances || [],
          activities: res?.activities || [],
          assignments: res?.assignments || [],
          summary: res?.summary || {
            attendanceCount: 0,
            activityCount: 0,
            assignmentCount: 0,
          },
        });
        setAttendancePage(0);
        setActivityPage(0);
        setAssignmentPage(0);
        setAbsentStudentList([]);
        setShowAbsentKey(null);
      })
      .catch(() => {
        setReport({
          attendances: [],
          activities: [],
          assignments: [],
          summary: { attendanceCount: 0, activityCount: 0, assignmentCount: 0 },
        });
        setAttendancePage(0);
        setActivityPage(0);
        setAssignmentPage(0);
      })
      .finally(() => setLoading(false));
  }, [userDetails?.schooleId, reportDate, classId, sectionId]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const onClassChange = (value) => {
    setClassId(value);
    setSectionId('');
    loadSections(value);
  };

  const openStudentsDialog = (row, type = 'assignment') => {
    const title = type === 'activity'
      ? row?.activityName || row?.areaOfWork || 'Activity'
      : row?.assignmentName || 'Assignment';
    setStudentsDialog({
      open: true,
      title,
      type,
      students: Array.isArray(row?.students) ? row.students : [],
    });
  };

  const closeStudentsDialog = () => {
    setStudentsDialog({ open: false, title: '', type: 'assignment', students: [] });
  };

  const pagedAttendances = paginateRows(report.attendances, attendancePage, attendanceRowsPerPage);
  const pagedActivities = paginateRows(report.activities, activityPage, activityRowsPerPage);
  const pagedAssignments = paginateRows(report.assignments, assignmentPage, assignmentRowsPerPage);

  if (String(userDetails?.role || '').toUpperCase() !== 'SCHOOLE') {
    return (
      <Box p={3}>
        <Typography color="error">This report is available for School Admin only.</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box className={classes.pageHeader}>
        <Box>
          <Typography className={classes.pageTitle}>Today&apos;s Teacher Report</Typography>
          {/* <Typography className={classes.pageSubtitle}>
            Attendance, activities and assignments for {reportDate}.
          </Typography> */}
        </Box>
        <Box display="flex" alignItems="center" gridGap={8}>
          <Chip
            size="small"
            className={classes.summaryChip}
            label={`Attendance: ${report.summary.attendanceCount || report.attendances.length}`}
          />
          <Chip
            size="small"
            className={classes.summaryChip}
            label={`Activities: ${report.summary.activityCount}`}
          />
          <Chip
            size="small"
            className={classes.summaryChip}
            label={`Assignments: ${report.summary.assignmentCount}`}
          />
          <Chip
            size="small"
            className={classes.updatedTodayChip}
            label={`Updated Today: ${(report.summary.activityUpdatedCount || 0) + (report.summary.assignmentUpdatedCount || 0)}`}
          />
          <Tooltip title="Refresh">
            <IconButton
              className={classes.iconBtnOutlined}
              onClick={loadReport}
              disabled={loading}
            >
              {loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Card className={classes.filtersCard} elevation={0}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={4} md={3}>
            <SchoolDatePicker
              label="Report date"
              value={reportDate}
              onChange={(iso) => setReportDate(iso)}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth variant="outlined" size="small">
              <Select displayEmpty value={classId} onChange={(e) => onClassChange(e.target.value)}>
                <MenuItem value=""><em>All Classes</em></MenuItem>
                {addClassList.map(({ _id, className }) => (
                  <MenuItem key={_id} value={_id}>{className}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth variant="outlined" size="small" disabled={!classId}>
              <Select displayEmpty value={sectionId} onChange={(e) => setSectionId(e.target.value)}>
                <MenuItem value=""><em>All Sections</em></MenuItem>
                {sectionList.map(({ _id, sectionName }) => (
                  <MenuItem key={_id} value={_id}>{sectionName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <Button variant="contained" color="primary" onClick={loadReport} disabled={loading}>
              Apply
            </Button>
          </Grid>
        </Grid>
      </Card>

      <Card className={classes.tableCard} elevation={0}>
        <SectionHeader
          title="Attendance"
          count={report.attendances.length}
          expanded={expandedSections.attendance}
          onToggle={() => toggleSection('attendance')}
        />
        <Collapse in={expandedSections.attendance} timeout="auto" unmountOnExit={false}>
          <TableContainer component={Paper} elevation={0} style={{ maxHeight: 500 }}>
            <Table stickyHeader size="small" className="mb-0 dashboard-table-row-hover">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.attendanceHeadCell}>Created By</TableCell>
                  <TableCell className={classes.attendanceHeadCell}>Class</TableCell>
                  <TableCell className={classes.attendanceHeadCell}>Section</TableCell>
                  <TableCell className={classes.attendanceHeadCell}>Total</TableCell>
                  <TableCell className={classes.attendanceHeadCell}>Present</TableCell>
                  <TableCell className={classes.attendanceHeadCell}>Absent</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && report.attendances.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" style={{ padding: 24, color: '#8a8a8a' }}>
                      Loading attendance…
                    </TableCell>
                  </TableRow>
                ) : report.attendances.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" style={{ padding: 24, color: '#8a8a8a' }}>
                      No attendance data for this date.
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedAttendances.map((row) => {
                    const absentKey = `${row.classId}-${row.sectionId || 'all'}`;
                    return (
                      <TableRow key={row._id || absentKey}>
                        <TableCell className={classes.attendanceBodyCell}>
                          {getCreatedByLabel(row)}
                        </TableCell>
                        <TableCell className={classes.attendanceBodyCell}>
                          {row.className || '—'}
                        </TableCell>
                        <TableCell className={classes.attendanceBodyCell}>
                          {row.sectionName || '—'}
                        </TableCell>
                        <TableCell className={classes.attendanceCountCell}>{row.totalStudents}</TableCell>
                        <TableCell className={classes.presentCount}>{row.presentStudents}</TableCell>
                        <TableCell
                          className={classes.absentCount}
                          onClick={() => handleAbsentClick(row)}
                        >
                          {row.absentStudents}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            rowsPerPageOptions={[5, 10, 25, 50]}
            count={report.attendances.length}
            page={attendancePage}
            rowsPerPage={attendanceRowsPerPage}
            onPageChange={(_, newPage) => setAttendancePage(newPage)}
            onRowsPerPageChange={(e) => {
              setAttendanceRowsPerPage(parseInt(e.target.value, 10));
              setAttendancePage(0);
            }}
          />
          {showAbsentKey && absentStudentList.length > 0 && (
            <Box display="flex" justifyContent="center" px={2} pb={2}>
              <Box className={classes.absentBox}>
                <strong style={{ color: '#cc0000', fontSize: '18px' }}>
                  Absent Students:
                </strong>
                <ul style={{ paddingLeft: '20px', color: '#cc0000', marginTop: '10px' }}>
                  {absentStudentList.map((student) => (
                    <li key={student.studentId}>{student.studentName}</li>
                  ))}
                </ul>
              </Box>
            </Box>
          )}
        </Collapse>
      </Card>

      <Card className={classes.tableCard} elevation={0}>
        <SectionHeader
          title="Teacher Activities"
          count={report.activities.length}
          expanded={expandedSections.activities}
          onToggle={() => toggleSection('activities')}
        />
        <Collapse in={expandedSections.activities} timeout="auto" unmountOnExit={false}>
          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={headerCellStyle}>Created By</TableCell>
                  {/* <TableCell style={headerCellStyle}>Updated By</TableCell> */}
                  <TableCell style={headerCellStyle}>Class</TableCell>
                  <TableCell style={headerCellStyle}>Section</TableCell>
                  <TableCell style={headerCellStyle}>Area Of Work</TableCell>
                  <TableCell style={headerCellStyle}>Activity</TableCell>
                  <TableCell style={headerCellStyle}>Exercise</TableCell>
                  <TableCell style={headerCellStyle}>Key</TableCell>
                  <TableCell style={headerCellStyle}>Added At</TableCell>
                  <TableCell style={headerCellStyle} align="center">Students</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.activities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" style={{ padding: 24, color: '#8a8a8a' }}>
                      No teacher activities for this date.
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedActivities.map((row) => (
                    <TableRow key={row._id}>
                      <TableCell>{getCreatedByLabel(row)}</TableCell>
                      {/* <TableCell>{getUpdatedByLabel(row)}</TableCell> */}
                      <TableCell>{row.className || '—'}</TableCell>
                      <TableCell>{row.sectionName || '—'}</TableCell>
                      <TableCell>{row.areaOfWork || '—'}</TableCell>
                      <TableCell style={{ fontWeight: 600, color: '#1a1a1a' }}>
                        {row.activityName || '—'}
                      </TableCell>
                      <TableCell>{row.exerciseName || '—'}</TableCell>
                      <TableCell>{row.key || '—'}</TableCell>
                      <TableCell>
                        {formatDateTime(row.createdAt)}
                        {row.isUpdatedToday && (
                          <Chip size="small" className={classes.updatedTodayChip} label="Updated Today" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          className={classes.viewBtn}
                          startIcon={<VisibilityIcon style={{ fontSize: 16 }} />}
                          onClick={() => openStudentsDialog(row, 'activity')}
                        >
                          View ({Array.isArray(row.students) ? row.students.length : 0})
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            rowsPerPageOptions={[5, 10, 25, 50]}
            count={report.activities.length}
            page={activityPage}
            rowsPerPage={activityRowsPerPage}
            onPageChange={(_, newPage) => setActivityPage(newPage)}
            onRowsPerPageChange={(e) => {
              setActivityRowsPerPage(parseInt(e.target.value, 10));
              setActivityPage(0);
            }}
          />
        </Collapse>
      </Card>

      <Card className={classes.tableCard} elevation={0}>
        <SectionHeader
          title="Teacher Assignments"
          count={report.assignments.length}
          expanded={expandedSections.assignments}
          onToggle={() => toggleSection('assignments')}
        />
        <Collapse in={expandedSections.assignments} timeout="auto" unmountOnExit={false}>
          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={headerCellStyle}>Created By</TableCell>
                  {/* <TableCell style={headerCellStyle}>Updated By</TableCell> */}
                  <TableCell style={headerCellStyle}>Assignment</TableCell>
                  <TableCell style={headerCellStyle}>Class</TableCell>
                  <TableCell style={headerCellStyle}>Section</TableCell>
                  <TableCell style={headerCellStyle}>Event Date</TableCell>
                  <TableCell style={headerCellStyle}>Due Date</TableCell>
                  <TableCell style={headerCellStyle}>Added At</TableCell>
                  <TableCell style={headerCellStyle} align="center">Students</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.assignments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" style={{ padding: 24, color: '#8a8a8a' }}>
                      No teacher assignments for this date.
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedAssignments.map((row) => (
                    <TableRow key={row._id}>
                      <TableCell>{getCreatedByLabel(row)}</TableCell>
                      {/* <TableCell>{getUpdatedByLabel(row)}</TableCell> */}
                      <TableCell style={{ fontWeight: 600, color: '#1a1a1a' }}>
                        {row.assignmentName || '—'}
                      </TableCell>
                      <TableCell>{row.className || '—'}</TableCell>
                      <TableCell>{row.sectionName || '—'}</TableCell>
                      <TableCell>{row.eventDate || '—'}</TableCell>
                      <TableCell>{row.dueDate || '—'}</TableCell>
                      <TableCell>
                        {formatDateTime(row.createdAt)}
                        {row.isUpdatedToday && (
                          <Chip size="small" className={classes.updatedTodayChip} label="Updated Today" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          className={classes.viewBtn}
                          startIcon={<VisibilityIcon style={{ fontSize: 16 }} />}
                          onClick={() => openStudentsDialog(row, 'assignment')}
                        >
                          View ({Array.isArray(row.students) ? row.students.length : 0})
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            rowsPerPageOptions={[5, 10, 25, 50]}
            count={report.assignments.length}
            page={assignmentPage}
            rowsPerPage={assignmentRowsPerPage}
            onPageChange={(_, newPage) => setAssignmentPage(newPage)}
            onRowsPerPageChange={(e) => {
              setAssignmentRowsPerPage(parseInt(e.target.value, 10));
              setAssignmentPage(0);
            }}
          />
        </Collapse>
      </Card>

      <Dialog open={studentsDialog.open} onClose={closeStudentsDialog} maxWidth="sm" fullWidth>
        <DialogTitle style={{ fontWeight: 700, color: '#1a1a1a' }}>
          Students — {studentsDialog.title}
        </DialogTitle>
        <DialogContent dividers>
          {studentsDialog.students.length === 0 ? (
            <Typography style={{ color: '#8a8a8a', padding: '12px 0' }}>
              No students linked to this {studentsDialog.type === 'activity' ? 'activity' : 'assignment'}.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={headerCellStyle}>#</TableCell>
                  <TableCell style={headerCellStyle}>Student Name</TableCell>
                  <TableCell style={headerCellStyle}>
                    {studentsDialog.type === 'activity' ? 'Status' : 'Submission Status'}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {studentsDialog.students.map((student, index) => (
                  <TableRow key={student._id || index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{student.studentName || '—'}</TableCell>
                    <TableCell>{student.status || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStudentsDialog} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
